import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useHistory } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useApi } from '../apiHelpers';
import useUserStore from '../stores/useUserStore';
import { useToast } from '../components/Toast/ToastContext';
import LogoSpinner from '../components/ui/LogoSpinner';
import EmptyState from '../components/ui/EmptyState';
import { MAP_TIP, MapTipStyle } from '../components/ui/PopoverMapTip';
import CompletionRing from '../components/CompletionRing';
import AirspaceStatusPill from '../components/AirspaceStatusPill';
import { DataTable } from '../components/table/DataTable';
import EventAirspaceModal from './EventAirspaceModal';
import { getDisplayStatus, parseDate, formatEventDate, businessToday } from '../utils/date-utils';
import { getShippingZone, isShowPast, EventHoverCardContent } from './EventHoverCard';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import Select from '../components/ui/Select';
import Tag from '../components/ui/Tag';
import Reveal from '../components/ui/Reveal';
import { Chip, ChipGroup } from '../components/ui/Chip';
import ChipToggle from '../components/ui/ChipToggle';
import Tabs from '../components/ui/Tabs';
import SearchField from '../components/ui/SearchField';
import UiTooltip from '../components/ui/Tooltip';

// Hex fills for map pins, chosen to visually match the Tailwind pill colors above.
// `past` is used for anything before today regardless of status (drained of meaning once done).
const STATUS_PIN_COLORS = {
  pending:   '#BBBBBB', // on-surface-variant
  confirmed: '#60A5FA', // blue-400
  packing:   '#C084FC', // purple-400
  active:    '#4ADE80', // green-400
  completed: '#34D399', // emerald-400
  cancelled: '#F87171', // red-400
  past:      '#5F5F5F', // outline, faded
};

// Zone outline colors — deeper/more saturated than status fills so the ring reads
// distinctly even when fill and stroke are both in the same hue family
// (e.g. active/green fill on a GREEN-zone pin).
const ZONE_STROKE_COLORS = {
  RED:    '#DC2626', // red-600
  YELLOW: '#D97706', // amber-600
  GREEN:  '#059669', // emerald-600
  PAST:   '#374151', // gray-700
};

const MAP_STAGES = ['collapsed', 'normal', 'fullscreen'];
const MAP_STAGE_STORAGE_KEY = 'events.mapStage';

// Time buckets for the "By Week" grouped view. `today` is a businessToday().
// Returns { key, label, order } where order controls the group display order
// (past sorts last). Each bucket is disjoint — an event lands in exactly one.
const getTimeBucket = (event, today) => {
  const d = parseDate(event.event_date);
  // Past is the END of the run; the forward buckets stay keyed off the START,
  // because "which week does this land in" is about when it begins.
  if (isShowPast(event, today)) return { key: 'past', label: 'Past', order: 99 };
  const endOfThisWeek  = today.endOf('week');                        // Saturday (dayjs default)
  const endOfNextWeek  = endOfThisWeek.add(1, 'week');
  const endOfThisMonth = today.endOf('month');
  const endOfNextMonth = today.add(1, 'month').endOf('month');
  if (!d.isAfter(endOfThisWeek))  return { key: 'this-week',  label: 'This Week',          order: 0 };
  if (!d.isAfter(endOfNextWeek))  return { key: 'next-week',  label: 'Next Week',          order: 1 };
  if (!d.isAfter(endOfThisMonth)) return { key: 'this-month', label: 'Rest of This Month', order: 2 };
  if (!d.isAfter(endOfNextMonth)) return { key: 'next-month', label: 'Next Month',         order: 3 };
  // Group remaining events by their individual month
  const monthLabel = d.format('MMMM YYYY');
  const monthOrder = 4 + d.diff(today, 'month');
  return { key: `month-${d.format('YYYY-MM')}`, label: monthLabel, order: monthOrder };
};

// Zone groups for the "By Zone" view — ordered by urgency (RED first).
const ZONE_GROUP_ORDER = { RED: 0, YELLOW: 1, GREEN: 2, PAST: 99 };
const ZONE_GROUP_LABEL = {
  RED:    'Red Zone (< 7 days)',
  YELLOW: 'Yellow Zone (< 14 days)',
  GREEN:  'Green Zone (14+ days)',
  PAST:   'Past',
};

const VIEW_MODES = [
  { key: 'list',   label: 'List' },
  { key: 'week',   label: 'By Week' },
  { key: 'zone',   label: 'By Zone' },
];
const VIEW_MODE_STORAGE_KEY = 'events.viewMode';

const getBuffered = (event) => {
  const ls = event.ls_drone_count || 0;
  const pyro = event.pyro_drone_count || 0;
  const bp = event.buffer_percentage || 0;
  const buffLs = ls + Math.ceil(ls * bp / 100);
  const buffPyro = pyro + Math.ceil(pyro * bp / 100);
  return { buffLs, buffPyro, total: buffLs + buffPyro };
};

const makePinSvg = (fill, size, stroke = '#000', strokeWidth = 0.5) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="${size}" height="${Math.round(size * 1.5)}">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" opacity="0.95"/>
    <circle cx="12" cy="12" r="5" fill="#000" opacity="0.3"/>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Icons are expensive-ish (base64 SVG + L.icon allocation). Cache by composite key
// so a 50-event map with only a handful of distinct (status × zone) combos allocates
// ~20 icons max, not ~100. Cache is module-level so it persists across re-renders.
const iconCache = new Map();
const getStatusIcon = (status, displayStatus, isPast, zoneLabel, highlighted) => {
  const colorKey = isPast ? 'past' : (STATUS_PIN_COLORS[displayStatus] ? displayStatus : 'pending');
  const fill   = isPast ? STATUS_PIN_COLORS.past : STATUS_PIN_COLORS[displayStatus] || STATUS_PIN_COLORS.pending;
  const stroke = ZONE_STROKE_COLORS[zoneLabel] || ZONE_STROKE_COLORS.PAST;
  const size = highlighted ? 30 : 20;
  const strokeWidth = highlighted ? 3 : 2;
  const key = `${colorKey}|${zoneLabel}|${size}|${highlighted ? 'h' : 'n'}`;
  let icon = iconCache.get(key);
  if (!icon) {
    icon = L.icon({
      iconUrl: makePinSvg(fill, size, stroke, strokeWidth),
      iconSize: [size, Math.round(size * 1.5)],
      iconAnchor: [size / 2, Math.round(size * 1.5)],
      tooltipAnchor: [0, -Math.round(size * 1.5)],
      popupAnchor: [0, -Math.round(size * 1.5) + 4],
    });
    iconCache.set(key, icon);
  }
  return icon;
};

// Combined initial-fit + stage-change handler. On every stage transition
// (mount counts as the first transition) we give the browser 60ms to paint
// the new container size, then call invalidateSize() so Leaflet re-measures,
// and re-fit to the current events. Replaces the old separate FitBounds
// component — MapStageBridge now handles both responsibilities.
const MapStageBridge = ({ stage, events }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      const coords = events
        .filter(e => e.launch_latitude && e.launch_longitude)
        .map(e => [e.launch_latitude, e.launch_longitude]);
      if (coords.length > 0) map.fitBounds(coords, { padding: [30, 30], maxZoom: 6 });
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  return null;
};

const EventMarker = ({ event, highlighted, onHover, onMarkerClick, today }) => {
  const markerRef = useRef(null);
  const displayStatus = getDisplayStatus(event);
  const isPast = isShowPast(event, today);
  const zone = getShippingZone(event.event_date, event.end_date);

  // Update icon when hover state changes. Using setIcon + ref (rather than
  // passing `icon` as a prop) avoids re-creating the Marker on every hover —
  // Leaflet keeps the same DOM element and just swaps the image URL.
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setIcon(getStatusIcon(event.status, displayStatus, isPast, zone.label, highlighted));
      markerRef.current.setZIndexOffset(highlighted ? 1000 : 0);
    }
  }, [highlighted, event.status, displayStatus, isPast, zone.label]);

  if (!event.launch_latitude || !event.launch_longitude) return null;

  return (
    <Marker
      ref={markerRef}
      position={[event.launch_latitude, event.launch_longitude]}
      icon={getStatusIcon(event.status, displayStatus, isPast, zone.label, false)}
      eventHandlers={{
        mouseover: () => onHover(event.id),
        mouseout:  () => onHover(null),
        click:     () => onMarkerClick(event.id),
      }}
    >
      <Tooltip direction="top" className={MAP_TIP} opacity={1}>
        <EventHoverCardContent event={event} geocodeEnabled={highlighted} />
      </Tooltip>
    </Marker>
  );
};

// Shared MapContainer payload used by both Normal and Fullscreen stages.
// Extracted so the two stage wrappers aren't copy-pasting 14 lines of JSX.
const EventMapInner = ({ mapEvents, today, hoveredId, setHoveredId, handleMarkerClick, stage }) => (
  <MapContainer center={[39.8, -98.5]} zoom={4} style={{ height: '100%', width: '100%' }} attributionControl={false}>
    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
    <MapStageBridge stage={stage} events={mapEvents} />
    {mapEvents.map(event => (
      <EventMarker
        key={event.id}
        event={event}
        today={today}
        highlighted={hoveredId === event.id}
        onHover={setHoveredId}
        onMarkerClick={handleMarkerClick}
      />
    ))}
  </MapContainer>
);

// Stage toggle — collapsed / normal / fullscreen. Rendered inside the map
// wrapper at top-right, above Leaflet's default pane stack (z ~400).
const MapStageControls = ({ stage, setStage }) => (
  <div className="absolute top-2 right-2 z-[20] inline-flex rounded-md bg-surface-container-high p-0.5 shadow-lg">
    <UiTooltip label="Collapse map">
    <button
      onClick={() => setStage('collapsed')}
      aria-label="Collapse map"
      className="px-2 py-1 rounded text-on-surface hover:text-white hover:bg-gray-700 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
      </svg>
    </button>
    </UiTooltip>
    <UiTooltip label="Normal size">
    <button
      onClick={() => setStage('normal')}
      aria-label="Normal size"
      aria-pressed={stage === 'normal'}
      className={`px-2 py-1 rounded transition-colors ${stage === 'normal' ? 'bg-primary text-black' : 'text-on-surface hover:text-white hover:bg-gray-700'}`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <rect x="4" y="7" width="16" height="10" rx="1.5" />
      </svg>
    </button>
    </UiTooltip>
    <UiTooltip label="Fullscreen">
    <button
      onClick={() => setStage('fullscreen')}
      aria-label="Fullscreen"
      aria-pressed={stage === 'fullscreen'}
      className={`px-2 py-1 rounded transition-colors ${stage === 'fullscreen' ? 'bg-primary text-black' : 'text-on-surface hover:text-white hover:bg-gray-700'}`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
      </svg>
    </button>
    </UiTooltip>
  </div>
);

// Fullscreen-only two-section legend: pin-fill by status, pin-outline by zone.
const MapLegend = () => (
  <div className="absolute bottom-4 left-4 z-[20] bg-surface-container-high rounded-md shadow-lg px-3 py-2 text-[11px]">
    <div className="flex gap-5">
      <div>
        <div className="text-on-surface-variant uppercase tracking-wider text-[9px] font-bold mb-1.5">Status (fill)</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {['pending','confirmed','packing','active','completed','cancelled','past'].map(s => (
            <div key={s} className="flex items-center gap-1.5 text-on-surface-variant capitalize">
              <span className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/40" style={{ background: STATUS_PIN_COLORS[s] }} />
              {s}
            </div>
          ))}
        </div>
      </div>
      <div className="border-l border-gray-700/60 pl-4">
        <div className="text-on-surface-variant uppercase tracking-wider text-[9px] font-bold mb-1.5">Zone (outline)</div>
        <div className="flex flex-col gap-1">
          {[
            { key: 'RED',    label: '< 7 days' },
            { key: 'YELLOW', label: '< 14 days' },
            { key: 'GREEN',  label: '14+ days' },
            { key: 'PAST',   label: 'past' },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-1.5 text-on-surface-variant">
              <span
                className="inline-block w-3 h-3 rounded-full bg-outline"
                style={{ border: `2px solid ${ZONE_STROKE_COLORS[key]}` }}
              />
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// The table
//
// The shared vessel hosts the show list: nine columns at module scope, so the
// double-click status editor keeps its <select> mounted mid-edit instead of
// being remounted by a fresh cell identity every render. Everything volatile —
// the tour names, which row is being edited, the graced ids, the three handlers
// — arrives on the table's `meta` and is read at render time. Never wrap one of
// these cells in React.memo: it would skip the parent's render and read a stale
// meta.
//
// The vessel owns the skin (tones, padding, the header band, the group bands),
// so nothing here restates a row border or a hover color. What stays the
// page's is what the MAP needs: the row's DOM id, the two hover handlers and
// the pin-click flash, all of which travel by `rowProps`.
//
// No `sortable`. This page's order is a view-mode (upcoming ascending, past
// descending, then bucketed by week or zone), which the page computes into the
// `data` prop — the sanctioned shape when the sort is not a column affordance.
//
// Plain JavaScript file: object literals, and <DataTable> with no type
// argument. The generic exists for the .tsx consumers and costs this file
// nothing.
// ---------------------------------------------------------------------------

// The only statuses a producer may set from the list. A show reaches the rest
// through its own page.
const EDITABLE_STATUSES = ['pending', 'confirmed', 'cancelled'];

// The band over a week or zone group. Content only: the vessel supplies the
// row, the colSpan and the microlabel type, so the label is bare text here.
const GroupBand = ({ label, count, color }) => (
  <span className="flex items-center gap-2">
    {label}
    {color && <span className={`inline-block w-2 h-2 rounded-full ring-1 ring-inset ${color}`} />}
    <span className="text-on-surface-variant">{count}</span>
  </span>
);

// The pill colours the old tinted classes carried, as data for the kit Tag.
const STATUS_HEX = {
  pending: '#9ca3af',
  confirmed: '#60a5fa',
  packing: '#c084fc',
  active: '#4ade80',
  completed: '#34d399',
  cancelled: '#f87171',
};
const ZONE_HEX = { RED: '#f87171', YELLOW: '#facc15', GREEN: '#4ade80', PAST: '#9ca3af' };

const STATUS_OPTIONS = EDITABLE_STATUSES.map(s => ({ key: s, label: s.charAt(0).toUpperCase() + s.slice(1) }));

// The list opens on mount: the cell's double-click is the whole gesture. Choosing hands the
// change up (which closes the editor and sets the grace); every other close just closes it.
const StatusEditor = ({ status, onStatusChange, onCloseEditing }) => {
  const chose = useRef(false);
  return (
    <Select
      label="Status"
      size="sm"
      width="fit"
      autoFocus
      defaultOpen
      options={STATUS_OPTIONS}
      value={status}
      onChange={(next) => { chose.current = true; onStatusChange(next); }}
      onClose={() => { if (!chose.current) onCloseEditing(); }}
      onBlur={onCloseEditing}
    />
  );
};

const EVENT_COLUMNS = [
  {
    id: 'event',
    header: 'Event',
    meta: { headerClass: 'w-[35%]' },
    cell: (info) => {
      const { tourMap } = info.table.options.meta;
      const event = info.row.original;
      return (
        <>
          <Link to={`/events/${event.id}`} className="text-on-surface font-medium hover:text-primary transition-colors duration-150">
            {event.name}
          </Link>
          {event.is_double_show && <Tag hex="#facc15" size="sm" className="ml-2 align-middle">2x</Tag>}
          {event.tour_id && (
            <UiTooltip label={tourMap[event.tour_id] || 'Tour'}>
              <Link to={`/events/tours/${event.tour_id}`}
                className="ml-2 inline-block max-w-[180px] truncate align-middle text-[10px] font-medium text-cyan-400/80 bg-cyan-400/10 px-1.5 py-0.5 rounded-full ring-1 ring-inset ring-cyan-400/20 hover:bg-cyan-400/15 transition-colors"
                onClick={e => e.stopPropagation()}>
                {tourMap[event.tour_id] || 'Tour'}
              </Link>
            </UiTooltip>
          )}
        </>
      );
    },
  },
  {
    id: 'date',
    header: 'Date',
    meta: { cellClass: 'text-on-surface whitespace-nowrap' },
    cell: ({ row }) => {
      const event = row.original;
      return (
        <>
          {formatEventDate(event.event_date, 'MMM D, YY')}
          {event.end_date && event.end_date !== event.event_date && (
            <span className="text-on-surface-variant"> &rarr; {formatEventDate(event.end_date, 'MMM D')}</span>
          )}
        </>
      );
    },
  },
  {
    // max-w-0 with the header's percentage width is what makes `truncate` bite
    // on a table cell: without it the cell grows to its content instead.
    id: 'location',
    header: 'Location',
    meta: { headerClass: 'w-[25%]', cellClass: 'text-on-surface-variant max-w-0 truncate' },
    cell: ({ row }) => row.original.location || <span className="text-on-surface-disabled">&mdash;</span>,
  },
  {
    id: 'drones',
    header: 'Drones',
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => {
      const event = row.original;
      const droneCount = (event.ls_drone_count || 0) + (event.pyro_drone_count || 0);
      const { total } = getBuffered(event);
      return (
        <>
          <span className="text-on-surface">{droneCount}</span>
          <span className="text-on-surface-variant mx-0.5">+</span>
          <span className="text-on-surface-variant">{total - droneCount}</span>
        </>
      );
    },
  },
  {
    // The completion ring rides in an unlabeled column: the ring IS the label,
    // and a header over it would name a thing the reader can already see.
    id: 'completion',
    header: '',
    meta: { align: 'center', headerClass: 'w-10' },
    // `: null`, never a bare `&&`. flexRender hands a cell renderer to React as
    // a component TYPE, and a component returning undefined throws on this
    // React — a show whose completion has not been computed yet would take the
    // page down.
    cell: ({ row }) => {
      const event = row.original;
      return event.completion ? (
        <div className="flex justify-center">
          <CompletionRing
            filled={event.completion.overall.filled}
            locked={event.completion.overall.locked || 0}
            total={event.completion.overall.total}
            attention={event.airspace_status === 'needs_action'}
          />
        </div>
      ) : null;
    },
  },
  {
    id: 'airspace',
    header: 'Airspace',
    meta: { align: 'center' },
    cell: (info) => {
      const { onAirspaceClick } = info.table.options.meta;
      const event = info.row.original;
      return (
        <AirspaceStatusPill
          status={event.airspace_status}
          size="table"
          onClick={(e) => { e.stopPropagation(); onAirspaceClick(event.id); }}
        />
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    // The double-click that opens the editor is a CELL-sized target, and the td
    // belongs to the vessel — so `cellAttrs` marks the cell and the handler
    // rides on `rowProps`, which can close over the page's canEdit. Putting it
    // on the pill instead would shrink the target to the pill's own box.
    meta: { align: 'center', cellAttrs: () => ({ 'data-event-status-cell': 'true' }) },
    cell: (info) => {
      const { editingStatusId, gracedIds, onStatusChange, onCloseEditing } = info.table.options.meta;
      const event = info.row.original;
      return editingStatusId === event.id ? (
        <StatusEditor
          status={event.status}
          onStatusChange={(next) => onStatusChange(event.id, next)}
          onCloseEditing={onCloseEditing}
        />
      ) : (
        <Tag
          hex={STATUS_HEX[getDisplayStatus(event)] || STATUS_HEX.pending}
          attention={gracedIds.has(event.id)}
          className="cursor-pointer capitalize"
        >
          {getDisplayStatus(event)}
        </Tag>
      );
    },
  },
  {
    id: 'zone',
    header: 'Zone',
    meta: { align: 'center' },
    cell: ({ row }) => {
      const zone = getShippingZone(row.original.event_date, row.original.end_date);
      return <Tag hex={ZONE_HEX[zone.label]}>{zone.label}</Tag>;
    },
  },
  {
    id: 'open',
    header: '',
    meta: { align: 'center', headerClass: 'w-8' },
    cell: ({ row }) => (
      <UiTooltip label="Open in new tab">
      <Link
        to={`/events/${row.original.id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        aria-label="Open in new tab"
        className="inline-flex items-center justify-center p-1 text-on-surface-variant hover:text-primary hover:bg-gray-700/50 rounded transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </Link>
      </UiTooltip>
    ),
  },
];

// "By Zone" needs no page data at all: the key IS the zone label, the order and
// the wording are the two static lookups above, and the dot's color comes off
// the group's first row — every row in the group carries the same zone, which
// is how the old hand-rolled band picked it too.
const ZONE_GROUP_BY = {
  key: (event) => getShippingZone(event.event_date, event.end_date).label,
  // The vessel's default is alphabetical, which here would put GREEN above RED.
  order: (keys) => [...keys].sort((a, b) => (ZONE_GROUP_ORDER[a] ?? 50) - (ZONE_GROUP_ORDER[b] ?? 50)),
  renderHeader: (key, rows) => {
    const first = rows[0];
    return (
      <GroupBand
        label={ZONE_GROUP_LABEL[key] || key}
        count={rows.length}
        color={first && getShippingZone(first.event_date, first.end_date).color}
      />
    );
  },
};

// The map's two row states, both page-owned because the map is.
//
// A hovered pin and a hovered row light the same row, so the tone is the
// vessel's OWN hover step: the two are indistinguishable, and a row already
// under the pointer does not repaint when the pin lights it as well.
const HOVER_SYNC_ROW = 'bg-surface-container-highest';
// A pin CLICK flashes its row for 1.6s. The inset ring is what carries the
// flash — it is a box-shadow, so nothing in the vessel's row classes touches
// it — and the wash rides along underneath.
const FLASH_ROW = 'ring-2 ring-inset ring-primary/60 bg-primary/10';

const EventList = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((state) => state.user);
  const canEdit = !!(user?.isadmin || user?.can_admin || user?.can_producer);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatuses, setActiveStatuses] = useState(new Set(['pending', 'confirmed', 'packing', 'active', 'completed']));
  const [search, setSearch] = useState('');
  const [showPast, setShowPast] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [airspaceModalEventId, setAirspaceModalEventId] = useState(null);
  const [gracedIds, setGracedIds] = useState(new Set());
  const [mondayCount, setMondayCount] = useState(0);
  const [tourMap, setTourMap] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    // Hydrate from localStorage; default to 'list' if unset or invalid.
    try {
      const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (saved && VIEW_MODES.some(v => v.key === saved)) return saved;
    } catch {}
    return 'list';
  });

  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode); } catch {}
  }, [viewMode]);

  // Map stage: 'collapsed' | 'normal' | 'fullscreen'. Only collapsed/normal
  // persist — fullscreen is a modal state, not a preference, so we deliberately
  // hydrate it back to 'normal' to avoid the map hijacking the page on next load.
  const [mapStage, setMapStage] = useState(() => {
    try {
      const saved = localStorage.getItem(MAP_STAGE_STORAGE_KEY);
      if (saved === 'collapsed' || saved === 'normal') return saved;
    } catch {}
    return 'normal';
  });

  useEffect(() => {
    // Don't write 'fullscreen' — we don't want to restore it on reload.
    if (mapStage === 'fullscreen') return;
    try { localStorage.setItem(MAP_STAGE_STORAGE_KEY, mapStage); } catch {}
  }, [mapStage]);

  // Esc closes fullscreen. Only attach the listener while fullscreen is active
  // so we don't intercept Esc for other keyboard flows elsewhere on the page.
  useEffect(() => {
    if (mapStage !== 'fullscreen') return;
    const onKey = (e) => { if (e.key === 'Escape') setMapStage('normal'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapStage]);

  // Lock body scroll while fullscreen so wheel events go to the map, not the page.
  useEffect(() => {
    if (mapStage !== 'fullscreen') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mapStage]);

  // Pin click behavior is stage-aware:
  //   - Normal: scroll the matching table row into view + brief flash. The
  //     sticky map stays visible above the row, so this is the primary
  //     "locate in list" affordance.
  //   - Fullscreen: navigate straight to /events/:id. Trying to scroll a list
  //     that's hidden behind a viewport-filling map was unintuitive — users
  //     who want details from a fullscreen pin just want to open the event.
  const history = useHistory();
  const [flashedId, setFlashedId] = useState(null);
  const flashTimerRef = useRef(null);
  const handleMarkerClick = useCallback((eventId) => {
    if (mapStage === 'fullscreen') {
      history.push(`/events/${eventId}`);
      return;
    }
    // Normal mode — scroll to row and flash it.
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlashedId(eventId);
    const el = document.getElementById(`event-row-${eventId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    flashTimerRef.current = setTimeout(() => {
      setFlashedId(null);
      flashTimerRef.current = null;
    }, 1600);
  }, [mapStage, history]);

  // Clean up any pending flash timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  // Save scroll position on unmount, restore after data loads.
  useEffect(() => {
    return () => {
      try { sessionStorage.setItem('eventlist-scroll', String(window.scrollY)); } catch {}
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    try {
      const saved = sessionStorage.getItem('eventlist-scroll');
      if (saved) {
        requestAnimationFrame(() => window.scrollTo(0, parseInt(saved)));
        sessionStorage.removeItem('eventlist-scroll');
      }
    } catch {}
  }, [loading]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await api.get('/events');
        setEvents(data || []);
      } catch (error) {
        toast.error('Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
    // Check for pending Monday imports
    api.get('/monday-inbox?status=pending')
      .then(data => setMondayCount(data?.length || 0))
      .catch(() => {});
    // Fetch tours for badge display
    api.get('/tours')
      .then(data => {
        const map = {};
        (data || []).forEach(t => { map[t.id] = t.name; });
        setTourMap(map);
      })
      .catch(() => {});
  }, []);

  const allStatuses = ['pending', 'confirmed', 'packing', 'active', 'completed', 'cancelled'];
  const today = useMemo(() => businessToday(), []);

  const toggleStatus = (status) => {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const handleStatusChange = async (eventId, newStatus) => {
    const oldStatus = events.find(e => e.id === eventId)?.status;
    // Optimistic update
    setEvents(evts => evts.map(e => e.id === eventId ? { ...e, status: newStatus } : e));
    // Keep visible if new status is filtered out
    if (!activeStatuses.has(newStatus)) {
      setGracedIds(g => new Set(g).add(eventId));
    }
    setEditingStatusId(null);
    try {
      await api.patch(`/events/${eventId}`, { body: { status: newStatus } });
    } catch {
      toast.error('Failed to update status');
      setEvents(evts => evts.map(e => e.id === eventId ? { ...e, status: oldStatus } : e));
      setGracedIds(g => { const next = new Set(g); next.delete(eventId); return next; });
    }
  };

  const closeEditing = () => {
    setEditingStatusId(null);
    setGracedIds(new Set());
  };

  const filtered = useMemo(() => {
    let result = events;

    // Status filter — use derived display status so "completed" filter works on active+past events
    result = result.filter(e => activeStatuses.has(getDisplayStatus(e)) || gracedIds.has(e.id));

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.name?.toLowerCase().includes(q) ||
        e.location?.toLowerCase().includes(q)
      );
    }

    // Split into upcoming and past, sort upcoming by date asc, past by date desc
    const upcoming = result.filter(e => !isShowPast(e, today)).sort((a, b) => parseDate(a.event_date).diff(parseDate(b.event_date)));
    const past = result.filter(e => isShowPast(e, today)).sort((a, b) => parseDate(b.event_date).diff(parseDate(a.event_date)));

    return { list: showPast ? [...upcoming, ...past] : upcoming, pastCount: past.length, upcomingCount: upcoming.length };
  }, [events, activeStatuses, gracedIds, search, showPast]);

  const { list: displayEvents, pastCount } = filtered;

  // "By Week" is the one view whose bands need page data: a bucket's wording and
  // its RANK both come off getTimeBucket, which needs `today`, and the vessel's
  // `order` callback sees only the keys. So one derivation here holds both —
  // which is what makes the band and the group order impossible to disagree
  // about. Events inside a bucket keep the upcoming-then-past order `filtered`
  // produced, because the table does no sorting of its own.
  const weekGroupBy = useMemo(() => {
    const buckets = new Map(); // key -> { key, label, order }
    for (const event of displayEvents) {
      const info = getTimeBucket(event, today);
      if (!buckets.has(info.key)) buckets.set(info.key, info);
    }
    return {
      key: (event) => getTimeBucket(event, today).key,
      // Through the page's own 0..99 ranks, never localeCompare: alphabetical
      // would read "Next Week, Past, Rest of This Month, This Week".
      order: (keys) => [...keys].sort((a, b) => (buckets.get(a)?.order ?? 99) - (buckets.get(b)?.order ?? 99)),
      renderHeader: (key, rows) => (
        <GroupBand label={buckets.get(key)?.label || key} count={rows.length} />
      ),
    };
  }, [displayEvents, today]);

  // 'list' passes no groupBy at all: the vessel renders the pile flat.
  const groupBy = viewMode === 'week' ? weekGroupBy : viewMode === 'zone' ? ZONE_GROUP_BY : undefined;

  // Only upcoming events with coordinates on the map
  const mapEvents = useMemo(() =>
    displayEvents.filter(e =>
      e.launch_latitude && e.launch_longitude &&
      !isShowPast(e, today)
    ),
    [displayEvents, today]
  );

  if (loading) return <LogoSpinner label="Loading events" />;

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4 space-y-5">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primary">Events</h1>
        <div className="flex gap-2.5">
          <Link to="/events/tours" className={buttonClass('tonal', 'md')}>Tours</Link>
          {canEdit && <Link to="/events/new" className={buttonClass('filled', 'md')}>+ New Event</Link>}
        </div>
      </div>

      {/* Monday Inbox Banner */}
      <Reveal show={mondayCount > 0}>
        <Link
          to="/events/monday-inbox"
          className="flex items-center gap-3 px-4 py-3 bg-orange-500/10 border border-orange-500/30 rounded-md hover:bg-orange-500/15 transition-colors duration-150"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-500/20 text-orange-400 text-xs font-bold">
            {mondayCount}
          </span>
          <span className="text-orange-300 text-sm font-medium">
            New from Monday — review and create events
          </span>
          <svg className="w-4 h-4 text-orange-400/60 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </Reveal>

      {/* Map — 3 stages: collapsed banner / sticky normal / fixed fullscreen */}
      {mapEvents.length > 0 && mapStage === 'collapsed' && (
        <button
          onClick={() => setMapStage('normal')}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-surface-container-low rounded-md hover:bg-surface-container transition-colors duration-150 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 text-on-surface-variant" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span>Show map</span>
          </span>
          <span className="text-xs text-on-surface-variant">{mapEvents.length} events</span>
        </button>
      )}

      {mapEvents.length > 0 && mapStage === 'normal' && (
        <div
          className="relative sm:sticky sm:top-0 sm:z-20 rounded-md overflow-hidden border border-gray-700/60 shadow-lg"
          style={{ height: 300 }}
        >
          <EventMapInner
            mapEvents={mapEvents}
            today={today}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            handleMarkerClick={handleMarkerClick}
            stage={mapStage}
          />
          <MapStageControls stage={mapStage} setStage={setMapStage} />
        </div>
      )}

      {/* Fullscreen is portaled to <body> so no ancestor can trap it via
          a stacking context (transform/filter/will-change on a parent would
          otherwise cause `position: fixed` to anchor to that ancestor
          instead of the viewport — which is what was clipping the navbar). */}
      {mapEvents.length > 0 && mapStage === 'fullscreen' && createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] bg-gray-900">
          <EventMapInner
            mapEvents={mapEvents}
            today={today}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            handleMarkerClick={handleMarkerClick}
            stage={mapStage}
          />
          <MapStageControls stage={mapStage} setStage={setMapStage} />
          <MapLegend />
          {/* Escape-hatch hint so the keyboard shortcut is discoverable */}
          <div className="absolute bottom-4 right-4 z-[20] px-2 py-1 bg-gray-800/80 border border-gray-700/60 rounded text-[10px] text-on-surface-variant backdrop-blur-sm">
            Press <kbd className="px-1 py-0.5 bg-gray-900/80 border border-gray-700 rounded text-on-surface text-[9px]">Esc</kbd> to close
          </div>
        </div>,
        document.body
      )}

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <SearchField
          label="Search events"
          value={search}
          onChange={setSearch}
          placeholder="Search events..."
          className="flex-1"
        />
        <ChipToggle checked={showPast} onChange={() => setShowPast(!showPast)}>
          {`Show past${pastCount > 0 ? ` (${pastCount})` : ''}`}
        </ChipToggle>
      </div>

      {/* Status filters + view toggle */}
      <div className="flex gap-3 flex-wrap items-center justify-between">
        <ChipGroup label="Status filter">
          {allStatuses.map(s => (
            <Chip key={s} pressed={activeStatuses.has(s)} onClick={() => toggleStatus(s)} count={events.filter(e => getDisplayStatus(e) === s).length} className="capitalize">
              {s}
            </Chip>
          ))}
        </ChipGroup>
        <Tabs
          label="View mode"
          size="sm"
          tabs={VIEW_MODES}
          value={viewMode}
          onChange={setViewMode}
        />
      </div>

      {/* Events table */}
      <DataTable
        data={displayEvents}
        columns={EVENT_COLUMNS}
        // By event id, so the row wearing the flash, the row being edited and
        // the row the map is pointing at all survive a refetch.
        getRowId={(event) => String(event.id)}
        meta={{
          tourMap,
          editingStatusId,
          gracedIds,
          onStatusChange: handleStatusChange,
          onCloseEditing: closeEditing,
          onAirspaceClick: setAirspaceModalEventId,
        }}
        groupBy={groupBy}
        // Everything the MAP needs from a row. The DOM id is what a pin click
        // scrolls to, the two handlers are the hover sync, and the class is the
        // flash. Flash and hover are ONE branch on purpose: two background
        // utilities of equal specificity would resolve by stylesheet order.
        rowProps={(event) => ({
          id: `event-row-${event.id}`,
          className: [
            'cursor-pointer',
            isShowPast(event, today) ? 'opacity-40' : '',
            flashedId === event.id ? FLASH_ROW : hoveredId === event.id ? HOVER_SYNC_ROW : '',
          ].filter(Boolean).join(' '),
          onMouseEnter: () => setHoveredId(event.id),
          onMouseLeave: () => setHoveredId(null),
          // Row click opens the event. Anything interactive inside the row keeps
          // its own behavior (the name Link does not stop propagation, so
          // without the closest() guard it would push twice). The status cell
          // is skipped only while it is editable: its double-click would
          // otherwise navigate away on the first click. A drag-selection ends in
          // a click too, so a non-empty selection is not a navigation.
          onClick: (e) => {
            if (e.target.closest('a,button,select,input,[role="option"],[role="listbox"]')) return;
            if (canEdit && e.target.closest('[data-event-status-cell]')) return;
            if (window.getSelection()?.toString()) return;
            if (e.metaKey || e.ctrlKey) {
              window.open(`/events/${event.id}`, '_blank', 'noopener');
              return;
            }
            history.push(`/events/${event.id}`);
          },
          // Cell-sized target for the status editor, on the row because the td
          // is the vessel's. Hit-tested rather than row-wide: double-clicking
          // the location text has to stay a text selection.
          onDoubleClick: canEdit
            ? (e) => {
                if (!e.target.closest('[data-event-status-cell]')) return;
                e.stopPropagation();
                setEditingStatusId(event.id);
              }
            : undefined,
        })}
        empty={<EmptyState variant="area">No events found</EmptyState>}
      />

      <MapTipStyle />
      {airspaceModalEventId && (
        <EventAirspaceModal
          eventId={airspaceModalEventId}
          onClose={() => setAirspaceModalEventId(null)}
        />
      )}
    </PageLayout>
  );
};

export default EventList;
